<template>
  <div class="input-group">
    <div class="form-control hls-input">
      <input
        class="form-control hls-input-url"
        type="url"
        :placeholder="findProtocol()"
        v-if="!loading && !uploading"
        v-model="hlsUrl"
        :disabled="readonly || disabled"
      />
      <input
        class="form-control hls-input-url hls-input-url-loading"
        type="text"
        placeholder="Uploading..."
        v-else-if="uploading"
        readonly
      />
      <input
        class="form-control hls-input-url hls-input-url-loading"
        type="text"
        placeholder="Preparing HLS..."
        v-else
        readonly
      />
      <div class="progress hls-upload-progress">
        <div
          class="progress-bar"
          role="progressbar"
          :style="'width:' + fileUploadPercentage + '%;'"
          :aria-valuenow="fileUploadPercentage"
          aria-valuemin="0"
          aria-valuemax="100"
        ></div>
      </div>
    </div>
    <div class="input-group-append" v-if="playBtn">
      <button
       
        type="button"
        title="Play"
        @click="openPlayModal"
        :disabled="loading || uploading"
      >Play
        <!-- <i class="cil-media-play"></i> -->
       
      </button>
    </div>
    <div class="input-group-append" v-if="uploadBtn && !readonly && !disabled">
      <button
        class="btn btn-block btn-outline-secondary hls-input-btn"
        type="button"
        @click="openUploadModal"
        :disabled="loading || uploading"
      >
        <i class="fa fa-upload"></i>
      </button>
    </div>

    <Modal
      title="Upload File"
      okBtnText="Upload"
      :show.sync="showUploadModal"
      :okBtnDisable="!isValidFile()"
      @ok="uploadFile()"
    >
      <form class="hls-upload-form">
        <div class="form-group row">
          <label class="col-sm-3 col-form-label col-form-label-sm"
            >Downloadable:</label
          >
          <div class="form-check form-check-inline">
            <input
              class="form-check-input"
              type="radio"
              value="false"
              v-model="downloadableVal"
              :disabled="!advanceOptions"
            />
            <label class="form-check-label">No</label>
          </div>
          <div class="form-check form-check-inline">
            <input
              class="form-check-input"
              type="radio"
              value="true"
              v-model="downloadableVal"
              :disabled="!advanceOptions"
            />
            <label class="form-check-label">Yes</label>
          </div>
        </div>
        <div class="form-group row">
          <label class="col-sm-3 col-form-label col-form-label-sm"
            >Access Level:</label
          >
          <div class="form-check form-check-inline">
            <input
              class="form-check-input"
              type="radio"
              value="private"
              v-model="mediaAccessLevelVal"
              :disabled="!advanceOptions"
            />
            <label class="form-check-label">Private</label>
          </div>
          <div class="form-check form-check-inline">
            <input
              class="form-check-input"
              type="radio"
              value="public"
              v-model="mediaAccessLevelVal"
              :disabled="!advanceOptions"
            />
            <label class="form-check-label">Public</label>
          </div>
        </div>
        <div class="form-group row">
          <label class="col-sm-3 col-form-label col-form-label-sm"
            >Media Type:</label
          >
          <div class="form-check form-check-inline">
            <input
              class="form-check-input"
              type="radio"
              value="audio"
              v-model="mediaTypeVal"
              :disabled="!mediaTypeChangeable"
            />
            <label class="form-check-label">Audio</label>
          </div>
          <div class="form-check form-check-inline">
            <input
              class="form-check-input"
              type="radio"
              value="video"
              v-model="mediaTypeVal"
              :disabled="!mediaTypeChangeable"
            />
            <label class="form-check-label">Video</label>
          </div>
        </div>

        <div class="form-group hls-upload-file">
          <input
            type="file"
            ref="uploadFile"
            :accept="acceptedTypes"
            @change="onFileChange"
          />
          <button
            :class="['btn', fileError ? 'btn-danger' : 'btn-success']"
            @click.stop.prevent="$refs['uploadFile'].click()"
          >
            <span v-if="!fileError"
              >Select {{ mediaTypeVal === "audio" ? "an" : "a" }}
              {{ mediaTypeVal }} file</span
            >
            <span v-else>Pleae Select a valid {{ mediaTypeVal }} file!</span>
          </button>
        </div>

        <div class="form-group hls-selected-file" v-if="file.name">
          <small class="text-muted"
            ><span>Selected file:</span>&nbsp;{{ file.name }}</small
          >
          <br />
          <small class="text-muted"
            ><span>Type:</span>&nbsp;{{ file.type }}</small
          >
        </div>
      </form>
    </Modal>

    <Modal title="Play" okBtnText="Done" :show.sync="showPlayModal">
      <HlsPlayer
        :type="mediaTypeVal"
        :src="hlsUrl"
        :play="showPlayModal"
        v-if="hlsUrl"
      />
      <div style="text-align: center" v-else>
        <i
          class="fa fa-volume-up fa-5x"
          aria-hidden="true"
          v-if="mediaTypeVal === 'audio'"
        ></i>
        <i
          class="fa fa-video-camera fa-5x"
          aria-hidden="true"
          v-else-if="mediaTypeVal === 'video'"
        ></i>
        <br />
        <br />
        <h4>Please upload a valid {{ mediaTypeVal }} file first.</h4>
      </div>
    </Modal>
  </div>
</template>

<script>
import axios from "axios";
import Modal from "@/containers/Modal.vue";
import HlsPlayer from "@/containers/HlsPlayer.vue";

export default {
  name: "HlsInput",
  components: {
    Modal,
    HlsPlayer,
  },
  props: {
    value: String,
    filedId: String,
    inProgress: {
      type: Boolean,
      default: false,
    },
    mediaTypeChangeable: {
      type: Boolean,
      default: false,
    },
    advanceOptions: {
      type: Boolean,
      default: false,
    },
    mediaType: {
      type: String,
      default: "audio",
      validator: (val) => ["audio", "video"].includes(val),
    },
    mediaTypeStrict: {
      type: Boolean,
      default: true,
    },
    mediaAccessLevel: {
      type: String,
      default: "private",
      validator: (val) => ["private", "public"].includes(val),
    },
    downloadable: {
      type: Boolean,
      default: false,
    },
    uploadUrl: String,
    fileDetailsUrl: String,
    hlsBaseUrl: String,

    // hlsBaseUrl: {
    //   type: String,
    //   default: 'http://media.aumhum.xyz/',
    // },
    readonly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    playBtn: {
      type: Boolean,
      default: true,
    },
    uploadBtn: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showUploadModal: false,
      showPlayModal: false,
      uploading: false,
      loading: false,

      hlsUrl: this.value,
      file: {},
      fileUploadPercentage: 0,
      fileId: "",
      fileError: false,

      // mediaTypeVal: this.mediaType,
      // mediaAccessLevelVal: this.mediaAccessLevel,
      // downloadableVal: this.downloadable,

      acceptAudio: "audio/mpeg,audio/mp4,audio/vnd.wav,audio/*",
      acceptVideo:
        "video/mp4,video/x-m4v,video/3gpp,video/quicktime,video/x-msvideo,video/x-flv,video/MP2T,video/*",
    };
  },
  computed: {
    acceptedTypes: function () {
      if (!this.mediaTypeStrict) {
        return this.acceptAudio + "," + this.acceptVideo;
      } else if (this.mediaTypeVal === "audio") {
        return this.acceptAudio;
      } else if (this.mediaTypeVal === "video") {
        return this.acceptVideo;
      } else {
        return "*/*";
      }
    },
    mediaTypeVal: function () {
      return this.mediaType;
    },
    mediaAccessLevelVal: function () {
      return this.mediaAccessLevel;
    },
    downloadableVal: function () {
      return this.downloadable;
    },
  },
  watch: {
    value(newValue) {
      this.hlsUrl = newValue;
    },
    loading(newLoading) {
      this.$emit("update:inProgress", newLoading || this.uploading);
    },
    uploading(newUploading) {
      this.$emit("update:inProgress", newUploading || this.loading);
    },
    hlsUrl(newHlsUrl) {
      this.$emit("update:value", newHlsUrl);
    },
    fileId(newFileId) {
      this.$emit("update:fileId", newFileId);
    },
  },
  methods: {
    openUploadModal() {
      this.file = {};
      this.fileError = false;
      this.showUploadModal = true;
    },
    openPlayModal() {
      this.showPlayModal = true;
    },
    onFileChange(e) {
      var files = e.target.files || e.dataTransfer.files;
      // console.log(files)
      if (!files.length) return;

      this.file = files[0];
      this.fileError = !this.isValidFile();
    },
    isValidFile() {
      let regex = /^.*\/.*/g;

      if (!this.mediaTypeStrict) {
        regex = /^(?:video|audio)\/.*/g;
      } else if (this.mediaTypeVal === "audio") {
        regex = /^audio\/.*/g;
      } else if (this.mediaTypeVal === "video") {
        regex = /^video\/.*/g;
      }

      if (regex.test(this.file.type)) {
        return true;
      }
      return false;
    },
    uploadFile() {
      let self = this;

      const formData = new FormData();
      formData.append("file", this.file);

      this.uploading = true;

      const regex = /{(.*?)}/gm;
      let matches = [...this.uploadUrl.matchAll(regex)];
      let uploadUrlFinal = matches.reduce((accumulateUrl, currentMatch) => {
        let param = this[currentMatch[1] + "Val"];
        if (param != undefined) {
          return accumulateUrl.replace(currentMatch[0], param);
        } else {
          return accumulateUrl;
        }
      }, this.uploadUrl);

      axios
        .post(uploadUrlFinal, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: function (progressEvent) {
            this.fileUploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
        .then((response) => {
          if (response.status === 200 && response.data) {
            console.log("upload response", response.data);

            let hlsUrlTmp = self.getHlsUrl(response.data.data);
            if (hlsUrlTmp) {
              console.log("hlsUrlTmp", hlsUrlTmp);
              // self.hlsUrl = hlsUrlTmp;
              self.setHlsUrl(hlsUrlTmp, response.data.fileId);
            } else if (response.data.fileId) {
              console.log("response.data.fileId", response.data.fileId);
              self.fileId = response.data.fileId;
              self.getFileDetails();
            }
          }
        })
        .catch(function (error) {
          console.log(error);
          alert("File upload failed!!!");
        })
        .then(function () {
          self.uploading = false;
          self.fileUploadPercentage = 0;
        });
    },
    getFileDetails() {
      console.log("**getFileDetails()");
      let self = this;
      self.loading = true;

      const regex = /{(.*?)}/gm;
      let matches = [...this.fileDetailsUrl.matchAll(regex)];
      let fileDetailsUrlFinal = matches.reduce(
        (accumulateUrl, currentMatch) => {
          let param = this[currentMatch[1]];
          if (param != undefined) {
            return accumulateUrl.replace(currentMatch[0], param);
          } else {
            return accumulateUrl;
          }
        },
        this.fileDetailsUrl
      );

      setTimeout(function () {
        self.loading = true;
        axios
          .get(fileDetailsUrlFinal)
          .then((response) => {
            if (response.status === 200 && response.data) {
              console.log(response.data);

              let hlsUrlTmp = self.getHlsUrl(response.data);
              if (hlsUrlTmp) {
                // self.hlsUrl = hlsUrlTmp;
                self.setHlsUrl(hlsUrlTmp, response.data.fileId);
              }
            }
          })
          .catch(function (error) {
            console.log(error);
            alert("File upload failed!!!");
          })
          .then(function () {
            self.loading = false;
          });
      }, 3 * 1000); // 3 seconds delay
    },
    getHlsUrl(data) {
    //  console.log("getHlsUrl()", data, data.transcoding.hls.url);
      if (
        data.transcoding &&
        data.transcoding.hls &&
        data.transcoding.hls.url
      ) {
        return data.transcoding.hls.url; //this.hlsBaseUrl.replace(/\/$/, "") + '/' + data.transcoding.hls.url;
      }
      return undefined;
    },
    setHlsUrl(url, fileId) {
      this.hlsUrl = url;
      this.fileId = fileId;
    },
    findProtocol() {
      let proto = window.location.href.match(/https|http/);
      if (proto && proto[0]) {
        return proto[0] + "://";
      } else {
        return "https://";
      }
    },
  },
};
</script>


<style scoped>
.form-group {
  margin-bottom: 0;
}

.hls-input {
  padding: 0;
}

.hls-input-url {
  border: none;
  height: auto;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.hls-input-url-loading {
  background: #fff;
}

.hls-upload-progress {
  height: 2px;
  background-color: transparent;
}

.hls-selected-file {
  line-height: 1;
}

.hls-input-btn {
  color: #5c6873;
  background-color: #f0f3f5;
  border-color: #e4e7ea;
}

.hls-upload-form {
  text-align: left;
}

.hls-upload-file {
  margin-top: 0.8rem;
  word-break: break-all;
}
.hls-upload-file input[type="file"] {
  display: none;
}
</style>
